import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { animate } from 'motion';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { useEnteredView } from '@fiverr-private/hooks';
import { PauseSolidIcon, PlaySolidIcon, SoundMaxIcon, SoundMutedIcon } from '@fiverr-private/visuals';
import { toVideoPreviewUrl, withVideoQuality, withVideoWidth } from '../../shared/cloudinary';
import useNetworkVideoQuality from '../../hooks/useNetworkVideoQuality';
import { MIXPANEL_EVENTS, sendMixpanelEvent } from '../../shared/tracking';
import { DIRECTORS_CTA_TEXT, DIRECTORS_SECTION_TITLE, HOLLYWOOD_DIRECTORS } from './constants';
import {
  activeThumb,
  clientLogo,
  clientsLabel,
  clientsLogosWrap,
  clientsLogosWrapTwoCol,
  clientsSection,
  credentialsDivider,
  credentialsLeftCol,
  credentialsRow,
  desktopCredentials,
  directorAvatar,
  directorBio,
  directorInfoSection,
  directorName,
  directorRole,
  dividerLine,
  fadeOut,
  fadeVisible,
  hoverImage,
  hoverPreview,
  mainVideo,
  mainVideoWrap,
  mainVideoWrapVertical,
  mainVideoVertical,
  mobileCredentialsAbove,
  mobileCredentialsBelow,
  muteButton,
  nameButton,
  nameRoleWrap,
  namesGrid,
  posterOverlay,
  quoteAuthorName,
  quoteAuthorTitle,
  quoteAuthorWrap,
  quoteCard,
  quoteCardAlone,
  quoteText,
  revealClip,
  section,
  showcaseWrap,
  videoColumn,
  bookCtaButton,
  tag,
  tagsWrap,
  thumbButton,
  thumbImage,
  thumbVideo,
  thumbVideoVisible,
  thumbs,
  title,
  videoControlButton,
  videoControlsRow,
} from './styles.ve.css';

const FADE_OUT_MS = 150;
const STAGGER_MS = 50;
const AVATAR_INDEX = 0;
const BIO_INDEX = 4;
const VERTICAL_BREAKPOINT = 900;

const useIsVertical = () => {
  const [isVertical, setIsVertical] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${VERTICAL_BREAKPOINT - 1}px)`);
    setIsVertical(mql.matches);

    const handler = (e: MediaQueryListEvent) => setIsVertical(e.matches);
    mql.addEventListener('change', handler);

    return () => mql.removeEventListener('change', handler);
  }, []);

  return isVertical;
};

interface DirectorsSectionProps {
  onStartProjectClick: () => void;
  scrollTo?: (target: HTMLElement | number, options?: { offset?: number }) => void;
}

const DirectorsSection: React.FC<DirectorsSectionProps> = ({ onStartProjectClick, scrollTo: scrollToFn }) => {
  const mainVideoRef = useRef<HTMLVideoElement>(null);
  const mainVideoWrapRef = useRef<HTMLDivElement>(null);
  const showcaseRef = useRef<HTMLDivElement>(null);
  const revealRefs = useRef<(HTMLDivElement | null)[]>([]);
  const videoRevealRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedDirectorId, setSelectedDirectorId] = useState(HOLLYWOOD_DIRECTORS[0].id);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [isMainVideoPlaying, setIsMainVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [fadePhase, setFadePhase] = useState<'idle' | 'out' | 'in'>('idle');
  const [hoverPreviewState, setHoverPreviewState] = useState<{ x: number; y: number; imageUrl: string } | null>(null);
  const [hoveredThumbId, setHoveredThumbId] = useState<string | null>(null);
  const thumbVideoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const isInViewportRef = useRef(false);
  const userPausedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const canHoverRef = useRef(typeof window !== 'undefined' ? window.matchMedia('(hover: hover)').matches : true);
  const trackingRef = useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const isVertical = useIsVertical();
  const networkQuality = useNetworkVideoQuality();
  const networkQualityRef = useRef(networkQuality);
  networkQualityRef.current = networkQuality;

  useEnteredView(trackingRef, {
    threshold: 0.1,
    onEntered: () => {
      sendMixpanelEvent({ eventName: MIXPANEL_EVENTS.DIRECTORS_SECTION_VIEWED });
    },
  });

  useEffect(() => {
    const element = mainVideoWrapRef.current;
    if (!element) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
        } else {
          setIsNearViewport(false);
          const videoElement = mainVideoRef.current;
          if (videoElement) {
            videoElement.pause();
            videoElement.removeAttribute('src');
            videoElement.load();
          }
          setIsMainVideoPlaying(false);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const selectedDirector = useMemo(
    () => HOLLYWOOD_DIRECTORS.find((director) => director.id === selectedDirectorId) || HOLLYWOOD_DIRECTORS[0],
    [selectedDirectorId]
  );

  const activeVideo = selectedVideoId
    ? selectedDirector.videos.find((video) => video.id === selectedVideoId) || selectedDirector.reel
    : selectedDirector.reel;

  const resolvedVideoUrl = useMemo(() => {
    if (!activeVideo) {
      return '';
    }

    let url = activeVideo.videoUrl;

    if (isVertical && activeVideo.videoUrlVertical) {
      url = activeVideo.videoUrlVertical;
    } else if (isVertical) {
      url = withVideoWidth(activeVideo.videoUrl, 400);
    }

    return withVideoQuality(url, networkQualityRef.current);
  }, [activeVideo, isVertical]);

  const isVerticalReel = isVertical && !!activeVideo?.videoUrlVertical && !selectedVideoId;

  useEffect(() => {
    setIsVideoReady(false);
  }, [activeVideo?.id, isVertical]);

  useEffect(() => {
    setSelectedVideoId(null);
  }, [selectedDirector]);

  useEffect(() => {
    if (!selectedVideoId) {
      return;
    }

    requestAnimationFrame(() => {
      const video = mainVideoRef.current;
      if (!video) {
        return;
      }

      const rect = video.getBoundingClientRect();
      if (rect.top < 0) {
        if (scrollToFn) {
          scrollToFn(video, { offset: -20 });
        } else {
          video.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });
  }, [selectedVideoId, scrollToFn]);

  useEffect(() => {
    userPausedRef.current = false;

    const videoElement = mainVideoRef.current;
    if (!videoElement) {
      return undefined;
    }

    if (isInViewportRef.current) {
      videoElement.play().catch(() => undefined);
      setIsMainVideoPlaying(true);
    }

    return () => {
      videoElement.pause();
      videoElement.removeAttribute('src');
      videoElement.load();
    };
  }, [resolvedVideoUrl]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    const wrapElement = mainVideoWrapRef.current;
    if (!wrapElement) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const videoElement = mainVideoRef.current;
        if (!videoElement) {
          return;
        }

        isInViewportRef.current = entry.isIntersecting;

        if (entry.isIntersecting && !userPausedRef.current) {
          videoElement.play().catch(() => undefined);
          setIsMainVideoPlaying(true);
        } else if (!entry.isIntersecting) {
          videoElement.pause();
          setIsMainVideoPlaying(false);
        }
      },
      { threshold: 0.6 }
    );

    observer.observe(wrapElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  const toggleMainVideo = useCallback(() => {
    const videoElement = mainVideoRef.current;
    if (!videoElement) {
      return;
    }

    if (videoElement.paused) {
      userPausedRef.current = false;
      videoElement.play().catch(() => undefined);
      setIsMainVideoPlaying(true);
      return;
    }

    userPausedRef.current = true;
    videoElement.pause();
    setIsMainVideoPlaying(false);
  }, []);

  const toggleMute = useCallback(() => {
    const videoElement = mainVideoRef.current;
    if (!videoElement) {
      return;
    }

    videoElement.muted = !videoElement.muted;
    setIsMuted(videoElement.muted);
  }, []);

  const hasClients = selectedDirector.clients && selectedDirector.clients.length > 0;
  const hasAwards = selectedDirector.awards && selectedDirector.awards.length > 0;
  const hasQuote = !!selectedDirector.quote;
  const hasCredentials = hasClients || hasAwards || hasQuote;
  const isTwoColClients = selectedDirector.clients && selectedDirector.clients.length >= 6;
  const clientsWrapClass = isTwoColClients ? clientsLogosWrapTwoCol : clientsLogosWrap;
  const clientsWrapStyle = isTwoColClients
    ? { gridTemplateRows: `repeat(${Math.ceil(selectedDirector.clients!.length / 2)}, auto)` }
    : undefined;

  useEffect(() => {
    if (fadePhase === 'in') {
      revealRefs.current.forEach((element, index) => {
        if (!element) {
          return;
        }

        if (index === AVATAR_INDEX) {
          animate(
            element,
            { opacity: [0, 1] },
            {
              duration: 0.5,
              ease: [0.25, 0.1, 0.25, 1],
            }
          );
        } else if (index === BIO_INDEX) {
          animate(
            element,
            { transform: ['translateY(40px)', 'translateY(0px)'], opacity: [0, 1] },
            {
              duration: 0.4,
              ease: [0.25, 0.1, 0.25, 1],
              delay: index * (STAGGER_MS / 1000),
            }
          );
        } else {
          animate(
            element,
            { transform: ['translateY(105%)', 'translateY(0%)'] },
            {
              duration: 0.5,
              ease: [0.25, 0.1, 0.25, 1],
              delay: index * (STAGGER_MS / 1000),
            }
          );
        }
      });

      videoRevealRefs.current.forEach((element, index) => {
        if (!element) {
          return;
        }

        animate(
          element,
          { transform: ['translateY(80px)', 'translateY(0px)'], opacity: [0, 1] },
          {
            duration: 0.5,
            ease: [0.25, 0.1, 0.25, 1],
            delay: index * (STAGGER_MS / 1000),
          }
        );
      });
    }

    if (fadePhase === 'out') {
      revealRefs.current.forEach((element, index) => {
        if (!element) {
          return;
        }

        if (index === AVATAR_INDEX) {
          element.style.opacity = '1';
        }

        element.style.transform = 'translateY(0%)';
      });

      videoRevealRefs.current.forEach((element) => {
        if (!element) {
          return;
        }

        element.style.transform = 'translateY(0px)';
        element.style.opacity = '1';
      });
    }
  }, [fadePhase]);

  const infoFadeClass = classNames({
    [fadeOut]: fadePhase === 'out',
    [fadeVisible]: fadePhase !== 'out',
  });

  const handleDirectorClick = (directorId: string) => {
    if (directorId === selectedDirectorId || fadePhase !== 'idle') {
      return;
    }

    const director = HOLLYWOOD_DIRECTORS.find((d) => d.id === directorId);

    sendMixpanelEvent({
      eventName: MIXPANEL_EVENTS.CLICKED_DIRECTOR,
      clickedElement: director?.name ?? directorId,
    });

    setHoverPreviewState(null);

    const target = showcaseRef.current;

    if (target) {
      if (scrollToFn) {
        scrollToFn(target, { offset: -50 });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    clearTimeout(timerRef.current);

    setFadePhase('out');

    timerRef.current = setTimeout(() => {
      setSelectedDirectorId(directorId);
      setFadePhase('in');

      timerRef.current = setTimeout(() => {
        setFadePhase('idle');
      }, 900);
    }, FADE_OUT_MS);
  };

  const handleDirectorMouseEnter = (event: React.MouseEvent<HTMLButtonElement>, imageUrl: string) => {
    if (!canHoverRef.current) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.left - 48;
    const y = rect.top - 96;

    setHoverPreviewState({ x, y, imageUrl });
  };

  const setRevealRef = (index: number) => (element: HTMLDivElement | null) => {
    revealRefs.current[index] = element;
  };

  const setVideoRevealRef = (index: number) => (element: HTMLDivElement | null) => {
    videoRevealRefs.current[index] = element;
  };

  return (
    <Container ref={trackingRef} className={section}>
      <h2 className={title}>{DIRECTORS_SECTION_TITLE}</h2>

      <Container className={namesGrid}>
        {HOLLYWOOD_DIRECTORS.map((director) => (
          <button
            key={director.id}
            className={nameButton}
            onMouseEnter={(event) => handleDirectorMouseEnter(event, director.hoverImageUrl)}
            onMouseLeave={() => setHoverPreviewState(null)}
            onClick={() => handleDirectorClick(director.id)}
            type="button"
          >
            <span>{director.name}</span>
          </button>
        ))}
      </Container>

      {hoverPreviewState ? (
        <Container
          className={hoverPreview}
          style={{ left: `${hoverPreviewState.x}px`, top: `${hoverPreviewState.y}px` }}
        >
          <img className={hoverImage} src={hoverPreviewState.imageUrl} alt="" aria-hidden />
        </Container>
      ) : null}

      <Container className={showcaseWrap} ref={showcaseRef}>
        <div className={classNames(directorInfoSection, infoFadeClass)}>
          <div className={revealClip}>
            <div ref={setRevealRef(0)}>
              <img
                className={directorAvatar}
                src={selectedDirector.avatarImageUrl}
                alt={selectedDirector.name}
                loading="lazy"
              />
            </div>
          </div>
          <Stack direction="column" gap="6">
            <div className={nameRoleWrap}>
              <div className={revealClip}>
                <div ref={setRevealRef(1)}>
                  <h3 className={directorName}>{selectedDirector.name}</h3>
                </div>
              </div>
              <div className={revealClip}>
                <div ref={setRevealRef(2)}>
                  <p className={directorRole}>{selectedDirector.role}</p>
                </div>
              </div>
            </div>
            <div className={revealClip}>
              <div ref={setRevealRef(3)}>
                <Container className={tagsWrap}>
                  {selectedDirector.tags.map((item) => (
                    <Text key={item} as="span" size="b_sm" fontWeight="semibold" className={tag}>
                      {item}
                    </Text>
                  ))}
                </Container>
              </div>
            </div>
            <div ref={setRevealRef(4)}>
              <p className={directorBio}>{selectedDirector.bio}</p>
            </div>
          </Stack>
        </div>

        {(hasClients || hasAwards) && (
          <div className={classNames(mobileCredentialsAbove, infoFadeClass)}>
            <div className={dividerLine} />
            {hasClients ? (
              <div className={clientsSection}>
                <p className={clientsLabel}>Clients</p>
                <div className={clientsWrapClass} style={clientsWrapStyle}>
                  {selectedDirector.clients!.map((client) => (
                    <span key={client} className={clientLogo}>
                      {client}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className={clientsSection}>
                <p className={clientsLabel}>Awards</p>
                <div className={clientsLogosWrap}>
                  {selectedDirector.awards!.map((award) => (
                    <span key={award} className={clientLogo}>
                      {award}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className={videoColumn}>
          <div ref={setVideoRevealRef(0)}>
            <div ref={mainVideoWrapRef} className={isVerticalReel ? mainVideoWrapVertical : mainVideoWrap}>
              <video
                key={`${activeVideo?.id}-${isVertical ? 'v' : 'h'}`}
                ref={mainVideoRef}
                className={isVerticalReel ? mainVideoVertical : mainVideo}
                src={isNearViewport ? resolvedVideoUrl : undefined}
                poster={isNearViewport ? activeVideo?.posterUrl : undefined}
                preload="metadata"
                muted={isMuted}
                loop
                playsInline
                onPlaying={() => setIsVideoReady(true)}
              />
              {!isVideoReady && activeVideo?.posterUrl && (
                <img src={activeVideo.posterUrl} alt="" className={posterOverlay} aria-hidden />
              )}
              <div className={videoControlsRow}>
                <button
                  type="button"
                  className={videoControlButton}
                  onClick={toggleMainVideo}
                  aria-label={isMainVideoPlaying ? 'Pause' : 'Play'}
                >
                  {isMainVideoPlaying ? (
                    <PauseSolidIcon size="sm" color="white" />
                  ) : (
                    <PlaySolidIcon size="sm" color="white" />
                  )}
                </button>
                <button
                  type="button"
                  className={muteButton}
                  onClick={toggleMute}
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <SoundMutedIcon size="sm" color="white" /> : <SoundMaxIcon size="sm" color="white" />}
                </button>
              </div>
            </div>
          </div>
          <div ref={setVideoRevealRef(1)}>
            <Container className={thumbs}>
              {selectedDirector.videos.map((video) => (
                <button
                  key={video.id}
                  type="button"
                  className={classNames(thumbButton, selectedVideoId === video.id && activeThumb)}
                  onClick={() => setSelectedVideoId(video.id)}
                  onMouseEnter={() => {
                    if (!canHoverRef.current) {
                      return;
                    }
                    setHoveredThumbId(video.id);
                    thumbVideoRefs.current[video.id]?.play().catch(() => undefined);
                  }}
                  onMouseLeave={() => {
                    const el = thumbVideoRefs.current[hoveredThumbId ?? ''];
                    if (el) {
                      el.pause();
                      el.currentTime = 0;
                    }
                    setHoveredThumbId(null);
                  }}
                >
                  <img className={thumbImage} src={video.thumbnailUrl} alt="" loading="lazy" />
                  <video
                    ref={(el) => {
                      thumbVideoRefs.current[video.id] = el;
                    }}
                    className={classNames(thumbVideo, hoveredThumbId === video.id && thumbVideoVisible)}
                    src={toVideoPreviewUrl(video.videoUrl)}
                    preload="none"
                    muted
                    playsInline
                    loop
                  />
                </button>
              ))}
            </Container>
          </div>
        </div>
      </Container>

      {hasCredentials && (
        <div className={classNames(infoFadeClass, desktopCredentials)}>
          <div className={credentialsDivider} />
          <div className={credentialsRow}>
            {(hasClients || hasAwards) && (
              <div className={credentialsLeftCol}>
                {hasClients && (
                  <div className={clientsSection}>
                    <p className={clientsLabel}>Clients</p>
                    <div className={clientsWrapClass} style={clientsWrapStyle}>
                      {selectedDirector.clients!.map((client) => (
                        <span key={client} className={clientLogo}>
                          {client}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {hasAwards && (
                  <div className={clientsSection}>
                    <p className={clientsLabel}>Awards</p>
                    <div className={clientsLogosWrap}>
                      {selectedDirector.awards!.map((award) => (
                        <span key={award} className={clientLogo}>
                          {award}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {hasQuote && (
              <div className={classNames(quoteCard, !hasClients && !hasAwards && quoteCardAlone)}>
                <p className={quoteText}>{selectedDirector.quote!.text}</p>
                <div className={quoteAuthorWrap}>
                  <p className={quoteAuthorName}>{selectedDirector.quote!.authorName}</p>
                  <p className={quoteAuthorTitle}>{selectedDirector.quote!.authorTitle}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {((hasClients && hasAwards) || hasQuote) && (
        <div className={classNames(infoFadeClass, mobileCredentialsBelow)}>
          {hasClients && hasAwards && (
            <>
              <div className={dividerLine} />
              <div className={clientsSection}>
                <p className={clientsLabel}>Awards</p>
                <div className={clientsLogosWrap}>
                  {selectedDirector.awards!.map((award) => (
                    <span key={award} className={clientLogo}>
                      {award}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
          {hasQuote && (
            <div className={quoteCard}>
              <p className={quoteText}>{selectedDirector.quote!.text}</p>
              <div className={quoteAuthorWrap}>
                <p className={quoteAuthorName}>{selectedDirector.quote!.authorName}</p>
                <p className={quoteAuthorTitle}>{selectedDirector.quote!.authorTitle}</p>
              </div>
            </div>
          )}
        </div>
      )}

      <Container marginTop="10">
        <button
          type="button"
          className={bookCtaButton}
          onClick={() => {
            sendMixpanelEvent({
              eventName: MIXPANEL_EVENTS.CLICKED_DIRECTOR_CTA,
              clickedElement: selectedDirector.name,
            });
            onStartProjectClick();
          }}
        >
          {DIRECTORS_CTA_TEXT}
        </button>
      </Container>
    </Container>
  );
};

export default DirectorsSection;
